import React from 'react';
import PropTypes from 'prop-types';
import { rgba } from 'polished';

import { withStyles } from '../tools/withStyles';
import { withSounds } from '../tools/withSounds';
import { Link } from '../components/Link';
import { Main } from '../components/Main';
import { Secuence } from '../components/Secuence';
import { Text } from '../components/Text';
import certificates from '../data/certificates';

const styles = theme => ({
  root: {},
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 16,
    marginTop: 24,
    maxWidth: 880,
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  },
  reveal: {
    opacity: 0,
    transform: 'translateY(24px)',
    transition: [
      `opacity ${theme.animation.time * 2}ms ease-out`,
      `transform ${theme.animation.time * 2}ms ease-out`
    ].join(',')
  },
  revealVisible: {
    opacity: 1,
    transform: 'translateY(0)'
  },
  card: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '56px minmax(0, 1fr)',
    alignItems: 'center',
    gap: 22,
    padding: 20,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.24),
    backgroundColor: rgba(theme.color.background.dark, 0.24),
    textDecoration: 'none',
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      borderColor: rgba(theme.color.secondary.main, 0.6),
      backgroundColor: rgba(theme.color.secondary.main, 0.05),
      boxShadow: `0 0 16px ${rgba(theme.color.secondary.main, 0.14)}`,
      transform: 'translateY(-2px)'
    }
  },
  logo: {
    width: 52,
    height: 52,
    flexShrink: 0,
    marginRight: 6,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.32),
    borderRadius: 8,
    backgroundColor: '#ffffff',
    backgroundSize: '76%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    boxShadow: `0 0 14px ${rgba(theme.color.secondary.main, 0.15)}`
  },
  content: {
    minWidth: 0
  },
  title: {
    margin: [0, 0, 6],
    fontSize: 16,
    lineHeight: 1.35,
    color: theme.color.primary.main,
    fontFamily: theme.typography.primary,
    textShadow: `0 0 4px ${rgba(theme.color.secondary.main, 0.5)}`
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 12,
    rowGap: 4,
    margin: [0, 0, 6],
    color: theme.color.secondary.main,
    fontSize: 13,
    lineHeight: 1.4,
    fontFamily: theme.typography.secondary
  },
  credential: {
    margin: 0,
    color: rgba(theme.color.text.main, 0.78),
    fontSize: 12,
    lineHeight: 1.4,
    wordBreak: 'break-all'
  }
});

const playSound = (sounds, name) => {
  const sound = sounds && sounds[name];

  if (sound) {
    sound.play();
  }
};

const REVEAL_TEXT_DELAY = 140;
const REVEAL_TEXT_DURATION = 420;

class ScrollReveal extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    sounds: PropTypes.object,
    children: PropTypes.func.isRequired
  };

  state = {
    visible: false,
    textVisible: false
  };

  componentDidMount () {
    if (!window.IntersectionObserver) {
      this.show();
      return;
    }

    this.observer = new window.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.show();
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.16
    });

    this.observer.observe(this.element);
  }

  componentWillUnmount () {
    clearTimeout(this.textTimer);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  show = () => {
    if (this.state.visible) {
      return;
    }

    playSound(this.props.sounds, 'fade');
    this.setState({ visible: true });
    this.textTimer = setTimeout(() => {
      this.setState({ textVisible: true });
    }, REVEAL_TEXT_DELAY);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render () {
    const { classes, children, sounds } = this.props;
    const { visible, textVisible } = this.state;

    return (
      <div
        className={`${classes.reveal} ${visible ? classes.revealVisible : ''}`}
        ref={ref => (this.element = ref)}
        onMouseEnter={() => playSound(sounds, 'hover')}
      >
        {children(textVisible)}
      </div>
    );
  }
}

const TextReveal = ({ children, visible }) => (
  <Text
    stableTime
    typingDuration={REVEAL_TEXT_DURATION}
    animation={{
      independent: true,
      show: visible,
      duration: REVEAL_TEXT_DURATION
    }}
  >
    {children}
  </Text>
);

TextReveal.propTypes = {
  children: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired
};

const CertificateCard = ({ classes, certificate, index, visible }) => {
  const credential = certificate.credentialId || '-';

  return (
    <Link
      className={classes.card}
      href={certificate.link}
      target={'certificate-' + index}
      rel='noreferrer'
    >
      <span
        className={classes.logo}
        style={{ backgroundImage: certificate.logo && `url(${certificate.logo})` }}
      />
      <span className={classes.content}>
        <h2 className={classes.title}>
          <TextReveal visible={visible}>{certificate.title}</TextReveal>
        </h2>
        <span className={classes.meta}>
          <span><TextReveal visible={visible}>{certificate.issuer}</TextReveal></span>
        </span>
        <p className={classes.credential}>
          <TextReveal visible={visible}>{'Credential ID: ' + credential}</TextReveal>
        </p>
      </span>
    </Link>
  );
};

CertificateCard.propTypes = {
  classes: PropTypes.object.isRequired,
  certificate: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  visible: PropTypes.bool.isRequired
};

class Sertifikalarim extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
    sounds: PropTypes.object
  };

  render () {
    const { classes, sounds } = this.props;

    return (
      <Main className={classes.root}>
        <Secuence stagger>
          <header>
            <h1><Text>Certificates</Text></h1>
          </header>
          <div className={classes.grid}>
            {certificates.map((certificate, index) => (
              <ScrollReveal
                key={certificate.title + certificate.date}
                classes={classes}
                sounds={sounds}
              >
                {visible => (
                  <CertificateCard
                    classes={classes}
                    certificate={certificate}
                    index={index}
                    visible={visible}
                  />
                )}
              </ScrollReveal>
            ))}
          </div>
        </Secuence>
      </Main>
    );
  }
}

export default withStyles(styles)(withSounds()(Sertifikalarim));

// Commit trace: 2025-07-19T02:51:54 - perf: optimize render loops in sertifikalarim.js

// Commit trace: 2025-12-12T03:47:37 - style: adjust formatting in sertifikalarim.js
